import { useContext, useMemo } from "react";

import { getWorkspaceConfig } from "@/data/workspaceConfig";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery";
import { fetchAccessibleAliasKeysQuery } from "@/models/repositories/WorkspaceCommandAlias";
import { UserContext } from "@/models/StateProviders/userProvider";

interface TeamAliasResult {
  team: string;
  availableTeams: string[];
  teamAliases: string[];
}

export function useTeamAliases(workspaceId: string, workspaceMembershipId: string): TeamAliasResult {
  const { dispatcherFunction } = useContext(UserContext);
  const workspaceConfig = getWorkspaceConfig(workspaceId);

  const accessibleAliasRows =
    useReactiveSelectQuery(
      fetchAccessibleAliasKeysQuery,
      [{ workspaceId, workspaceMembershipId }],
      "useTeamAliases:accessibleAliasKeys",
    ) ?? [];

  const availableTeams = useMemo(() => {
    if (!workspaceId || !workspaceConfig?.commandAliasFunctionMapping) return [];
    const accessibleKeys = new Set(accessibleAliasRows.map((r) => r.alias.toLowerCase()));
    return [
      ...new Set(
        Object.entries(workspaceConfig.commandAliasFunctionMapping)
          .filter(([key]) => accessibleKeys.has(key.toLowerCase()))
          .map(([, fn]) => fn),
      ),
    ];
  }, [workspaceConfig?.commandAliasFunctionMapping, workspaceId, accessibleAliasRows]);

  const team = useMemo(() => {
    if (dispatcherFunction && availableTeams.includes(dispatcherFunction)) {
      return dispatcherFunction;
    }
    return availableTeams[0] || "";
  }, [dispatcherFunction, availableTeams]);

  const teamAliases = useMemo(() => {
    if (!team || !workspaceConfig?.commandAliasFunctionMapping) return [];
    const fromConfig = Object.entries(workspaceConfig.commandAliasFunctionMapping)
      .filter(([, fn]) => fn === team)
      .map(([alias]) => alias.toLowerCase());
    return [...new Set([...fromConfig, team.toLowerCase()])];
  }, [team, workspaceConfig?.commandAliasFunctionMapping]);

  return { team, availableTeams, teamAliases };
}
